import {
  makeStyles,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import {
  CollapseContainerRow,
  CollapseGroupRows,
  DefaultTable,
  FirstCell,
  NestedRow,
  NoCollapseGroupRows,
  TextCell,
} from "@View/common/table";
import ButtonBB from "@View/components/buttons/ButtonBB";
import LabeledSwitch from "@View/components/common/LabeledSwitch";
import React, { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { NestedTable } from "./NestedTable";
import EditCellModal from "./EditCellModal";
import HistoryTooltip from "./HistoryTooltip";
import { mutationOfTableHandlers } from "@Utils/tableUtils";
import { canAddAbility, canEditEverything } from "@Security/stateHelpers";
import { getHighlightColorForLastIndex } from "@Utils/colorPicker";
import { useCTAButtonStyle } from "@View/style/components/tooltipStyles";
import { getLabel } from "@Utils/translationHelpers";
import getAPI from "@API/index";

// Стили вынесены за пределы компонента для оптимизации производительности
const useEditRowStyles = makeStyles(() => ({
  input: {
    background: "inherit",
    paddingTop: "0px !important",
    "& .MuiFormControl-root ": {
      height: "100%",
    },
    "& .MuiFormControl-root .MuiInputBase-root": {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "inherit",
    },
    "& .MuiFormControl-root .Mui-focused": {
      outline: "rgb(25, 118, 210) solid 1px",
      outlineffset: "-1px",
    },
    "& input": {
      textAlign: "right",
      padding: "0",
    },
    "& .MuiInput-underline:before": {
      border: "none",
    },
    "& .MuiInput-underline:after": {
      border: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      border: "none",
    },
    "& .MuiInput-root input": {
      color: "inherit",
    },
  },
}));

const FinTable2 = ({
  transformedData,
  isHorAnEn,
  originalData,
  noTransformData,
  isVerticalAnalyseEnable,
  frequency,
  isEdit,
  setIsEdit,
  startDate,
  endDate,
  pageType,
  formType = "Default",
}) => {
  const { t } = useTranslation();
  const { id: finDataRequestId, customerId, consolidationId } = useParams();
  const [data, setData] = React.useState();
  const counter = React.useRef({ ...transformedData });
  const [originFinStatements, setOriginFinStatements] = React.useState();
  const [originalDataButton, setOriginalDataButton] = React.useState(true);
  const [disabled, setDisabled] = React.useState(true);
  const [clicked, setClicked] = React.useState(false);
  // Состояния для истории изменений (только для consolidation)
  const [selectedCell, setSelectedCell] = React.useState(null); // { path, index, displayPath, position, oldValue, newValue, rowLabel }
  const [cellComments, setCellComments] = React.useState({}); // { "path": "comment" }
  // Состояние проверки консолидации (Status = 3 = Verified)
  const [isVerified, setIsVerified] = React.useState(false);

  const IsConsolidation = () => {
    return pageType === "consolidation";
  };

  // Преобразование ответа консолидированной расшифровки в формат RecursiveTableData
  const transformConsolidationTranscription = (response) => {
    const dates = response.dates || [];
    const total = response.total || {};

    const values = (response.values || []).map((item) => {
      const counterparties = (item.values || []).map((cp) => {
        // Если контрагент состоит из нескольких компаний — добавляем уровень вложенности
        if (cp.companyBreakdown && cp.companyBreakdown.length > 0) {
          return {
            colors: {},
            itemName: cp.title,
            total: cp.values || {},
            values: cp.companyBreakdown.map((company) => ({
              colors: {},
              title: company.clientName,
              values: company.values || {},
            })),
          };
        }
        return {
          colors: {},
          title: cp.title,
          values: cp.values || {},
        };
      });

      return {
        colors: {},
        itemName: item.itemName,
        total: item.total || {},
        values: counterparties,
      };
    });

    return { colors: {}, dates, total, values };
  };

  const getData = () => {
    setClicked(true);
    setOriginalDataButton(!originalDataButton);
    if (originalDataButton === true) {
      getEditData();
    } else {
      getOriginalData();
    }
  };

  useEffect(() => {
    getData();
    // Получаем статус консолидации при загрузке
    if (pageType === "consolidation") {
      fetchConsolidationStatus();
    }
  }, []);

  // Получение информации о консолидации (метаданные)
  const fetchConsolidationStatus = async () => {
    try {
      const url = `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}/info`;
      const response = await getAPI().request({ url });
      // Status 3 = Verified
      setIsVerified(response?.status === 3);
    } catch (e) {
      // Если эндпоинт не существует, просто игнорируем
      console.log('Could not fetch consolidation info');
    }
  };

  // Изменение статуса верификации
  const updateVerificationStatus = async (newVerified) => {
    try {
      const newStatus = newVerified ? 3 : 1; // 3 = Verified, 1 = Completed
      const url = `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}/status?status=${newStatus}`;
      await getAPI().request({
        url,
        method: "PATCH",
      });
      setIsVerified(newVerified);
    } catch (e) {
      console.error('Failed to update verification status:', e);
    }
  };

  useEffect(() => {
    let asyncFunc = async () => {
      if (originFinStatements) {
        let trs = originalData(originFinStatements.finData);
        setData(trs);
      }
    };
    asyncFunc();
  }, [frequency, isHorAnEn, startDate, endDate]);

  useEffect(() => {
    let asyncFunc = async () => {
      if (originFinStatements) {
        let trs = originalData(originFinStatements.finData);
        if (data !== undefined) {
          if (
            typeof data.balanceSheet.assets.currentAssets
              .cashEquivalents[1][0] !==
            typeof trs.balanceSheet.assets.currentAssets.cashEquivalents[1][0]
          ) {
            setData({ ...trs });
          }
        }
      }
    };
    asyncFunc();
  }, [isVerticalAnalyseEnable, data]);

  const getOriginalData = async () => {
    const url = IsConsolidation()
      ? `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}?isOriginal=true`
      : `/api/FinDataRequest/${finDataRequestId}/financialStatements?isOriginal=true`;

    const response = await getAPI().request({ url });
    setOriginFinStatements(JSON.parse(JSON.stringify(response)));

    let d = originalData(response.finData);
    setData(d);

    setClicked(false);
    return originFinStatements;
  };

  const getEditData = async () => {
    const url = IsConsolidation()
      ? `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}?isOriginal=false`
      : `/api/FinDataRequest/${finDataRequestId}/financialStatements?isOriginal=false`;

    const response = await getAPI().request({ url });
    setOriginFinStatements(JSON.parse(JSON.stringify(response)));

    let d = originalData(response.finData);
    setData(undefined);
    setData(d);

    setClicked(false);
    return originFinStatements;
  };

  const intangibleAssets = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/intangibleAssets`,
    });
  const fixedAssets = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/fixedassets`,
    });
  const receivables = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/receivables`,
    });
  const financialInvestments = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/financialinvestments`,
    });
  const nonCurrentFinancialInvestments = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/noncurrentfinancialinvestments`,
    });
  const longTermLoans = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/longtermloans`,
    });
  const longTermLoansOther = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/longtermdutiesother`,
    });
  const shortTermLoans = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/shorttermloans`,
    });
  const accountsPayable = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/accountspayable`,
    });
  const intangibleSearchAssets = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/intangiblesearchassets`,
    });
  const stocks = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/stocks`,
    });
  const cashequivalents = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/cashequivalents`,
    });
  const revenue = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/revenue`,
    });
  const otherincome = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/otherincome`,
    });
  const otherExpenses = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/otherexpenses`,
    });
  const administrativeExpenses = async () =>
    getAPI().request({
      url: `/api/FinDataRequest/${finDataRequestId}/transcription/administrativeexpenses`,
    });

  // Фабрика fetch-функций для консолидированных расшифровок
  const createConsolidationTranscriptionFetch = (type) => async () => {
    const url = `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}/transcription/${type}`;
    const response = await getAPI().request({ url });
    return transformConsolidationTranscription(response);
  };

  const consolidationReceivables = createConsolidationTranscriptionFetch("receivables");
  const consolidationNoncurrentfinancialinvestments = createConsolidationTranscriptionFetch("noncurrentfinancialinvestments");
  const consolidationFinancialInvestments = createConsolidationTranscriptionFetch("financialinvestments");
  const consolidationLongTermLoans = createConsolidationTranscriptionFetch("longtermloans");
  const consolidationShortTermLoans = createConsolidationTranscriptionFetch("shorttermloans");
  const consolidationAccountsPayable = createConsolidationTranscriptionFetch("accountspayable");

  // Преобразование пути к полю для API (balanceSheet.assets... -> BalanceSheet.Assets...)
  const toApiFieldPath = (path) => {
    return path
      .split(".")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(".");
  };

  // Получить историю изменений для конкретного поля
  const getFieldHistory = (path, dataIndex) => {
    if (!originFinStatements?.finData) return null;
    // Ищем по columnName, т.к. API возвращает все записи без фильтрации по recordType/частоте,
    // а transformedData может показывать только подмножество (например, только годовые).
    // Прямая индексация [dataIndex] даёт смещение на другие даты.
    const expectedColumnName = transformedData.header[dataIndex + 1];
    const finDataItem = originFinStatements.finData.find(
      (item) => item.columnName === expectedColumnName
    );
    if (!finDataItem?.fieldHistory) return null;
    const apiPath = toApiFieldPath(path);
    return finDataItem.fieldHistory[apiPath] || null;
  };



  const EditRow = ({ finData, k, level, isEdit, firstCell, rowLabelOverride }) => {
    const classes = useEditRowStyles();

    function setValueWithDate(finData, path, index, value) {
      let obj = {
        date: finData.header[index],
        value: value,
      };

      Object.prototype.getValue = function (path2, value) {
        if (typeof path2 === "string") {
          var last = (path2 = path2.split(".")).splice(-1);
          [this].concat(path2).reduce(function (a, b) {
            return a[b];
          })[last] = value;
        }
      };
      // Удаляем пробелы и заменяем запятую на точку для корректного преобразования числа
      const cleanValue = value ? String(value).replace(/\s/g, '').replace(',', '.') : '0';
      const numericValue = Number(cleanValue) || 0;
      // Обновляем все записи с той же датой (разные частотности)
      noTransformData.forEach((el) => {
        if (el.columnName === obj.date) {
          el.getValue(path, numericValue);
        }
      });

      return obj;
    }

    function setValue(finData, path, index, value) {
      if (isEdit === true) {
        Object.prototype.getValue = function (path2, value) {
          if (typeof path2 === "string") {
            var last = (path2 = path2.split(".")).splice(-1);
            [this].concat(path2).reduce(function (a, b) {
              return a[b];
            })[last] = value;
          }
        };

        const properties = path.split(".");
        let getData = finData;
        properties.forEach((el) => (getData = getData[el]));
        getData[index] = value;

        let way = [];
        properties.forEach((el, i) => way.push(el));
        way = way.reduce((a, b) => a + "." + b);
        let fullObj = finData;
        fullObj.getValue(way, getData);
        return fullObj;
      }
    }

    const handleChange = (c, i, k) => {
      if (!i) return;
      counter.current = setValue(data, k, i, c.target.value);
      setValueWithDate(data, k, i, c.target.value);
      setDisabled(false);
    };

    // Обработчик клика для выбора ячейки
    const handleCellClick = (event, i, k, currentValue) => {
      console.log('handleCellClick called:', { isEdit, i, k, currentValue });
      if (isEdit && i !== 0) {
        const rect = event.currentTarget.getBoundingClientRect();
        const apiPath = toApiFieldPath(k);
        console.log('Setting selectedCell:', { apiPath, i });
        setSelectedCell({
          path: apiPath,
          index: i,
          displayPath: k,
          position: { top: rect.bottom - 4, left: rect.right - 40 },
          oldValue: currentValue,
          newValue: currentValue,
          rowLabel: rowLabelOverride || firstCell || k,
        });
      }
    };

    const legendValue = finData[0];
    const dataValues = finData.slice(1);

    return (
      <>
        {firstCell && <FirstCell level={level}>{firstCell}</FirstCell>}

        {/* Legend / Row Number Column (Always Layout Column 2) */}
        <TextCell
          key={0}
          className={classes.input}
          color={getHighlightColorForLastIndex(legendValue, 0, finData, isHorAnEn)}
          minWidth={(String(legendValue || "").length * 10) + "px"}
          style={{
            cursor: "default"
          }}
        >
          <span
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
              height: "100%",
              color: getHighlightColorForLastIndex(legendValue, 0, finData, isHorAnEn, true),
            }}
          >
            {legendValue}
          </span>
        </TextCell>

        {/* Data Columns */}
        {dataValues.map((e, index) => {
          const i = index + 1; // Actual index in finData
          const arr = finData;

          if (e === undefined || e === null) {
            e = 0;
          }

          if (typeof e === "object" && typeof e[0] === "object") {
            // Handle complex object items (existing logic)
            return (
              <TextCell
                key={i}
                className={classes.input}
                color={getHighlightColorForLastIndex(e, i, arr, isHorAnEn)}
                minWidth={e.length * 10 + "px"}
              >
                {e}
              </TextCell>
            );
          }

          const history = getFieldHistory(k, i - 1);
          const hasHistory = history && history.length > 0;

          // Debug log
          if (i === 1) {
            console.log('History debug:', { k, dataIndex: i - 1, history, hasHistory, isConsolidation: IsConsolidation() });
          }

          return (
            <TextCell
              key={i}
              className={classes.input}
              color={getHighlightColorForLastIndex(e, i, arr, isHorAnEn)}
              minWidth={String(e).length * 10 + "px"}
              style={{ position: "relative", cursor: isEdit ? "pointer" : "default" }}
              onClick={(event) => handleCellClick(event, i, k, e)}
            >
              <HistoryTooltip history={history}>
                <span style={{ display: "flex", width: "100%", height: "100%" }}>
                  {hasHistory && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: 0,
                        height: 0,
                        borderTop: "10px solid #f44336",
                        borderLeft: "10px solid transparent",
                        zIndex: 1,
                        pointerEvents: "auto",
                      }}
                    />
                  )}
                  {isEdit ? (
                    <TextField
                      style={{
                        width: "100%",
                        color: getHighlightColorForLastIndex(
                          e,
                          i,
                          arr,
                          isHorAnEn,
                          true
                        ),
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      inputProps={{
                        style: { textAlign: "right" },
                      }}
                      defaultValue={e}
                    />
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        color: getHighlightColorForLastIndex(
                          e,
                          i,
                          arr,
                          isHorAnEn,
                          true
                        ),
                      }}
                    >
                      {e}
                    </span>
                  )}
                </span>
              </HistoryTooltip>
            </TextCell>
          );
        })}
      </>
    );
  };

  const onSubmit = async () => {
    setIsEditCover(!isEdit);
    try {
      const url = IsConsolidation()
        ? `/api/customer/${customerId}/consolidation/${consolidationId || finDataRequestId}`
        : `/api/FinDataRequest/${finDataRequestId}/data`;

      const requestData = { finData: noTransformData, fieldComments: cellComments };

      await getAPI().request({
        url: url,
        method: "PUT",
        data: requestData,
      });

      // Обновляем данные после сохранения
      await getEditData();

      // Обновляем статус консолидации (после сохранения статус меняется на Edited)
      if (IsConsolidation()) {
        await fetchConsolidationStatus();
      }

      // Очищаем состояния
      setCellComments({});
      setSelectedCell(null);
    } catch (e) {
      // do nothing
    }
  };

  // Применить изменение из popup - принимает данные из модалки
  const applyChange = useCallback((modalData) => {
    if (!modalData) return;

    const { displayPath, index, newValue, comment, path } = modalData;

    // Обновляем данные через существующие функции
    Object.prototype.getValue = function (path2, value) {
      if (typeof path2 === "string") {
        var last = (path2 = path2.split(".")).splice(-1);
        [this].concat(path2).reduce(function (a, b) {
          return a[b];
        })[last] = value;
      }
    };

    // Обновляем transformed data для отображения
    const properties = displayPath.split(".");
    let getData = data;
    properties.forEach((el) => (getData = getData[el]));
    getData[index] = newValue;
    counter.current = { ...data };

    // Обновляем noTransformData для API — все записи с той же датой (разные частотности)
    const cleanValue = newValue ? String(newValue).replace(/\s/g, '').replace(',', '.') : '0';
    const numericValue = Number(cleanValue) || 0;
    const date = transformedData.header[index];
    noTransformData.forEach((el) => {
      if (el.columnName === date) {
        el.getValue(displayPath, numericValue);
      }
    });

    // Сохраняем комментарий (ключ включает дату столбца для различения комментариев на разные даты)
    if (comment) {
      const date = transformedData.header[index];
      setCellComments(prev => ({
        ...prev,
        [`${path}::${date}`]: comment,
      }));
    }

    setDisabled(false);
    setSelectedCell(null);
  }, [data, transformedData, noTransformData]);

  const setIsEditCover = async () => {
    const newIsEdit = !isEdit;
    setIsEdit(newIsEdit);

    // При включении режима редактирования загружаем отредактированные данные
    if (newIsEdit && originalDataButton) {
      // Если сейчас показываются оригинальные данные, переключаемся на отредактированные
      setOriginalDataButton(false);
      await getEditData();
    }

    setTimeout(() => {
      if (typeof mutationOfTableHandlers.last === "function") {
        mutationOfTableHandlers.last();
      }
    }, 100);
  };

  const btnStyles = useCTAButtonStyle();

  if (data === undefined) return <></>;

  return (
    <>
      {canEditEverything() && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "#fff",
            padding: "16px 20px",
            borderTop: "1px solid #E1E6F1",
            boxSizing: "border-box",
          }}
        >
          {canAddAbility() && (
            <>
              <div style={{ marginRight: "20px" }}>
                <LabeledSwitch
                  checked={isEdit}
                  onChange={() => setIsEditCover()}
                  labelOn="Редактирование"
                  labelOff="Редактирование"
                />
              </div>
              <div style={{ marginRight: "10px" }}>
                <ButtonBB disabled={disabled} onClick={() => onSubmit()}>
                  Сохранить
                </ButtonBB>
              </div>
            </>
          )}
          <div style={{ marginRight: "10px" }}>
            <ButtonBB
              disabled={isEdit}
              style={{
                opacity: isEdit ? "0.5" : "1",
                ...(!originalDataButton
                  ? { background: "#fff", color: "#54606F" }
                  : {}),
              }}
              className={originalDataButton ? btnStyles.default : ""}
              onClick={() => getData()}
            >
              {originalDataButton
                ? "Показать не оригинал"
                : "Показать оригинал"}
            </ButtonBB>
          </div>

          {/* Свитчер "Проверено" справа (только для консолидаций) */}
          {IsConsolidation() && (
            <div style={{ marginLeft: "auto" }}>
              <LabeledSwitch
                checked={isVerified}
                onChange={() => updateVerificationStatus(!isVerified)}
                labelOn="Проверено"
                labelOff="Проверено"
              />
            </div>
          )}
        </div>
      )}

      <DefaultTable fixTwoCell maxCellWidth="110px">
        <TableHead
          style={{
            background: "#F3F5F7",
            height: "46px",
          }}
        >
          <TableRow>
            <TableCell
              style={{
                width: "280px",
                minWidth: "280px",
                paddingLeft: "calc(2em + 6px)",
                fontWeight: 600,
                fontSize: "13px",
                lineHeight: "19px",
                color: "#54606F",
                textAlign: "left",
                border: "none",
              }}
            >
              {t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.balanceSheet"
              )}
            </TableCell>
            {transformedData.header.map((el, index) => {
              // Get included/excluded companies for this date from originFinStatements
              // index 0 is row number (legend), dates start from index 1, so use index - 1
              const dateInfo = index > 0 ? originFinStatements?.dates?.[index - 1] : null;
              const includedCompanies = dateInfo?.includedCompanies || [];
              const excludedCompanies = dateInfo?.excludedCompanies || [];
              const hasExcluded = excludedCompanies.length > 0;
              const totalCompanies = includedCompanies.length + excludedCompanies.length;

              // Build tooltip text
              let tooltipText = "";
              if (includedCompanies.length > 0) {
                tooltipText += `Включены в расчёт:\n${includedCompanies.map(c => `  • ${c.name}`).join('\n')}`;
              }
              if (excludedCompanies.length > 0) {
                if (tooltipText) tooltipText += "\n\n";
                tooltipText += `Исключены из расчёта:\n${excludedCompanies.map(c => `  • ${c.name} (${c.reason})`).join('\n')}`;
              }

              return (
                <TableCell
                  style={{
                    fontWeight: 600,
                    fontSize: "13px",
                    lineHeight: "16px",
                    letterSpacing: "0.0075em",
                    color: "#54606F",
                    paddingRight: "20px",
                    paddingLeft: "20px",
                  }}
                  component="th"
                  key={index}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
                    {el}
                    {index > 0 && hasExcluded && (
                      <span
                        style={{
                          color: "#ff9800",
                          fontSize: "1.2em",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        title={tooltipText}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* <NoCollapseGroupRows color={'#fff'} paddingLeft={'2em'} cellText={t("finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.balanceSheet")}></NoCollapseGroupRows> */}

          <CollapseGroupRows
            k={"balanceSheet.assets.total"}
            EditRow={EditRow}
            isEdit={isEdit}
            colapse={true}
            isOpen={false}
            level={3}
            clickableCellText={t(
              "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.assets"
            )}
            otherCellsTexts={data.balanceSheet.assets.total}
          >
            <CollapseGroupRows
              k={"balanceSheet.assets.nonCurrentAssets.total"}
              EditRow={EditRow}
              isEdit={isEdit}
              level={4}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.nonCurrentAssets"
              )}
              otherCellsTexts={data.balanceSheet.assets.nonCurrentAssets.total}
            >
              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={data.balanceSheet.assets.nonCurrentAssets.goodwill}
                  k={"balanceSheet.assets.nonCurrentAssets.goodwill"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.goodwill"
                  )}
                  level={5}
                />
              </NestedRow>

              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.assets.nonCurrentAssets.intangibleAssets}
                    k={"balanceSheet.assets.nonCurrentAssets.intangibleAssets"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.intangibleAssets"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.assets.nonCurrentAssets.intangibleAssets"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.intangibleAssets"
                  )}
                  otherCellsTexts={
                    data.balanceSheet.assets.nonCurrentAssets.intangibleAssets
                  }
                >
                  <NestedTable fetchEffect={intangibleAssets} />
                </CollapseContainerRow>
              )}

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.nonCurrentAssets.randDResults
                  }
                  k={"balanceSheet.assets.nonCurrentAssets.randDResults"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.randDResults"
                  )}
                  level={5}
                />
              </NestedRow>

              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.assets.nonCurrentAssets.intangibleSearchAssets}
                    k={"balanceSheet.assets.nonCurrentAssets.intangibleAssets"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.intangibleSearchAssets"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.assets.nonCurrentAssets.intangibleAssets"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.intangibleSearchAssets"
                  )}
                  otherCellsTexts={
                    data.balanceSheet.assets.nonCurrentAssets
                      .intangibleSearchAssets
                  }
                >
                  <NestedTable fetchEffect={intangibleSearchAssets} />
                </CollapseContainerRow>
              )}

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.nonCurrentAssets
                      .tangibleSearchAssets
                  }
                  k={
                    "balanceSheet.assets.nonCurrentAssets.tangibleSearchAssets"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.tangibleSearchAssets"
                  )}
                  level={5}
                />
              </NestedRow>

              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.assets.nonCurrentAssets.fixedAssets}
                    k={"balanceSheet.assets.nonCurrentAssets.fixedAssets"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.fixedAssets"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.assets.nonCurrentAssets.fixedAssets"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    getLabel(t, "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.fixedAssets", formType)
                  )}
                  otherCellsTexts={
                    data.balanceSheet.assets.nonCurrentAssets.fixedAssets
                  }
                >
                  <NestedTable fetchEffect={fixedAssets} />
                </CollapseContainerRow>
              )}

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.nonCurrentAssets
                      .profitableInvestments
                  }
                  k={
                    "balanceSheet.assets.nonCurrentAssets.profitableInvestments"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.profitableInvestments"
                  )}
                  level={5}
                />
              </NestedRow>

              <CollapseContainerRow
                k={
                  "balanceSheet.assets.nonCurrentAssets.financialInvestments"
                }
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={
                  getLabel(t, "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.financialInvestments", formType)
                }
                otherCellsTexts={
                  data.balanceSheet.assets.nonCurrentAssets
                    .financialInvestments
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationNoncurrentfinancialinvestments : nonCurrentFinancialInvestments} />
              </CollapseContainerRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.nonCurrentAssets.deferredTax
                  }
                  k={"balanceSheet.assets.nonCurrentAssets.deferredTax"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.deferredTax"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={data.balanceSheet.assets.nonCurrentAssets.other}
                  k={"balanceSheet.assets.nonCurrentAssets.other"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.other"
                  )}
                  level={5}
                />
              </NestedRow>
            </CollapseGroupRows>
            <CollapseGroupRows
              k={"balanceSheet.assets.currentAssets.total"}
              EditRow={EditRow}
              isEdit={isEdit}
              level={4}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.currentAssets"
              )}
              otherCellsTexts={data.balanceSheet.assets.currentAssets.total}
            >
              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.assets.currentAssets.stocks}
                    k={"balanceSheet.assets.currentAssets.stocks"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.stocks"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.assets.currentAssets.stocks"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.stocks"
                  )}
                  otherCellsTexts={data.balanceSheet.assets.currentAssets.stocks}
                >
                  <NestedTable fetchEffect={stocks} />
                </CollapseContainerRow>
              )}

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={data.balanceSheet.assets.currentAssets.longTermAssetsForSale}
                  k={"balanceSheet.assets.currentAssets.longTermAssetsForSale"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermAssetsForSale"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.currentAssets
                      .valueAddedTaxOnAcquiredValuables
                  }
                  k={
                    "balanceSheet.assets.currentAssets.valueAddedTaxOnAcquiredValuables"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.valueAddedTaxOnAcquiredValuables"
                  )}
                  level={5}
                />
              </NestedRow>

              <CollapseContainerRow
                k={"balanceSheet.assets.currentAssets.receivables"}
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.receivables"
                )}
                otherCellsTexts={
                  data.balanceSheet.assets.currentAssets.receivables
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationReceivables : receivables} />
              </CollapseContainerRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={data.balanceSheet.assets.currentAssets.s1231}
                  k={"balanceSheet.assets.currentAssets.s1231"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.s1231"
                  )}
                  level={5}
                />
              </NestedRow>

              <CollapseContainerRow
                k={
                  "balanceSheet.assets.currentAssets.currentAssetFinancialInvestments"
                }
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={
                  getLabel(t, "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.currentAssetFinancialInvestments", formType)
                }
                otherCellsTexts={
                  data.balanceSheet.assets.currentAssets
                    .currentAssetFinancialInvestments
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationFinancialInvestments : financialInvestments} />
              </CollapseContainerRow>

              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.assets.currentAssets.cashEquivalents}
                    k={"balanceSheet.assets.currentAssets.cashEquivalents"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.cashEquivalents"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.assets.currentAssets.cashEquivalents"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.cashEquivalents"
                  )}
                  otherCellsTexts={
                    data.balanceSheet.assets.currentAssets.cashEquivalents
                  }
                >
                  <NestedTable fetchEffect={cashequivalents} />
                </CollapseContainerRow>
              )}

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.assets.currentAssets.currentAssetOther
                  }
                  k={"balanceSheet.assets.currentAssets.currentAssetOther"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.currentAssetOther"
                  )}
                  level={5}
                />
              </NestedRow>
            </CollapseGroupRows>
          </CollapseGroupRows>

          <CollapseGroupRows
            k={"balanceSheet.liabilities.total"}
            EditRow={EditRow}
            isEdit={isEdit}
            colapse={true}
            isOpen={false}
            level={3}
            clickableCellText={t(
              "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.liabilities"
            )}
            otherCellsTexts={data.balanceSheet.liabilities.total}
          >
            <CollapseGroupRows
              k={"balanceSheet.liabilities.capitalAndReserves.total"}
              EditRow={EditRow}
              isEdit={isEdit}
              level={4}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.capitalAndReserves"
              )}
              otherCellsTexts={
                data.balanceSheet.liabilities.capitalAndReserves.total
              }
            >
              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves
                      .authorizedCapital
                  }
                  k={
                    "balanceSheet.liabilities.capitalAndReserves.authorizedCapital"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.authorizedCapital"
                  )}
                  level={4}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves.ownShares
                  }
                  k={"balanceSheet.liabilities.capitalAndReserves.ownShares"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.ownShares"
                  )}
                  level={4}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves
                      .revaluationOfNonCurrentAssets
                  }
                  k={
                    "balanceSheet.liabilities.capitalAndReserves.revaluationOfNonCurrentAssets"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.revaluationOfNonCurrentAssets"
                  )}
                  level={4}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves
                      .additionalCapital
                  }
                  k={
                    "balanceSheet.liabilities.capitalAndReserves.additionalCapital"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.additionalCapital"
                  )}
                  level={4}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves
                      .reserveCapital
                  }
                  k={
                    "balanceSheet.liabilities.capitalAndReserves.reserveCapital"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.reserveCapital"
                  )}
                  level={4}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.capitalAndReserves
                      .retainedEarnings
                  }
                  k={
                    "balanceSheet.liabilities.capitalAndReserves.retainedEarnings"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.retainedEarnings"
                  )}
                  level={4}
                />
              </NestedRow>
            </CollapseGroupRows>

            <CollapseGroupRows
              k={"balanceSheet.liabilities.longTermDuties.total"}
              EditRow={EditRow}
              isEdit={isEdit}
              level={4}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermDuties"
              )}
              otherCellsTexts={
                data.balanceSheet.liabilities.longTermDuties.total
              }
            >
              <CollapseContainerRow
                k={
                  "balanceSheet.liabilities.longTermDuties.longTermBorrowedFunds"
                }
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermBorrowedFunds"
                )}
                otherCellsTexts={
                  data.balanceSheet.liabilities.longTermDuties
                    .longTermBorrowedFunds
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationLongTermLoans : longTermLoans} />
              </CollapseContainerRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.longTermDuties
                      .longTermDeferredTax
                  }
                  k={
                    "balanceSheet.liabilities.longTermDuties.longTermDeferredTax"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermDeferredTax"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.longTermDuties
                      .longTermEstimated
                  }
                  k={
                    "balanceSheet.liabilities.longTermDuties.longTermEstimated"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermEstimated"
                  )}
                  level={5}
                />
              </NestedRow>
              {IsConsolidation() ? (
                <NestedRow style={{ height: "50px" }}>
                  <EditRow
                    finData={data.balanceSheet.liabilities.longTermDuties.longTermOther}
                    k={"balanceSheet.liabilities.longTermDuties.longTermOther"}
                    isEdit={isEdit}
                    firstCell={t(
                      "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermOther"
                    )}
                    level={5}
                  />
                </NestedRow>
              ) : (
                <CollapseContainerRow
                  k={"balanceSheet.liabilities.longTermDuties.longTermOther"}
                  EditRow={EditRow}
                  isEdit={isEdit}
                  isHorAnEn={isHorAnEn}
                  level={5}
                  clickableCellText={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.longTermOther"
                  )}
                  otherCellsTexts={
                    data.balanceSheet.liabilities.longTermDuties.longTermOther
                  }
                >
                  <NestedTable fetchEffect={longTermLoansOther} />
                </CollapseContainerRow>
              )}
            </CollapseGroupRows>

            <CollapseGroupRows
              k={"balanceSheet.liabilities.shortTermCommitments.total"}
              EditRow={EditRow}
              isEdit={isEdit}
              level={4}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.shortTermCommitments"
              )}
              otherCellsTexts={
                data.balanceSheet.liabilities.shortTermCommitments.total
              }
            >
              <CollapseContainerRow
                k={
                  "balanceSheet.liabilities.shortTermCommitments.shortTermBorrowedFunds"
                }
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.shortTermBorrowedFunds"
                )}
                otherCellsTexts={
                  data.balanceSheet.liabilities.shortTermCommitments
                    .shortTermBorrowedFunds
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationShortTermLoans : shortTermLoans} />
              </CollapseContainerRow>
              <CollapseContainerRow
                k={
                  "balanceSheet.liabilities.shortTermCommitments.accountsPayable"
                }
                EditRow={EditRow}
                isEdit={isEdit}
                isHorAnEn={isHorAnEn}
                level={5}
                clickableCellText={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.accountsPayable"
                )}
                otherCellsTexts={
                  data.balanceSheet.liabilities.shortTermCommitments
                    .accountsPayable
                }
              >
                <NestedTable fetchEffect={IsConsolidation() ? consolidationAccountsPayable : accountsPayable} />
              </CollapseContainerRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.shortTermCommitments
                      .revenueOfTheFuturePeriods
                  }
                  k={
                    "balanceSheet.liabilities.shortTermCommitments.revenueOfTheFuturePeriods"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.revenueOfTheFuturePeriods"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.shortTermCommitments.s1531
                  }
                  k={"balanceSheet.liabilities.shortTermCommitments.s1531"}
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.s1531"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.shortTermCommitments
                      .shortTermEstimated
                  }
                  k={
                    "balanceSheet.liabilities.shortTermCommitments.shortTermEstimated"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.shortTermEstimated"
                  )}
                  level={5}
                />
              </NestedRow>

              <NestedRow style={{ height: "50px" }}>
                <EditRow
                  finData={
                    data.balanceSheet.liabilities.shortTermCommitments
                      .shortTermOther
                  }
                  k={
                    "balanceSheet.liabilities.shortTermCommitments.shortTermOther"
                  }
                  isEdit={isEdit}
                  firstCell={t(
                    "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.shortTermOther"
                  )}
                  level={5}
                />
              </NestedRow>
            </CollapseGroupRows>
          </CollapseGroupRows>

          <CollapseGroupRows
            paddingLeft={"2em"}
            cellStyles={{
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "19px",
              paddingLeft: "15px",
            }}
            noClickableCellText={t(
              "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.financialResults"
            )}
          ></CollapseGroupRows>

          {IsConsolidation() ? (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.revenue}
                k={"financialResults.revenue"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.revenue"
                )}
                level={3}
              />
            </NestedRow>
          ) : (
            <CollapseContainerRow
              k={"financialResults.revenue"}
              EditRow={EditRow}
              isEdit={isEdit}
              isHorAnEn={isHorAnEn}
              level={3}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.revenue"
              )}
              otherCellsTexts={data.financialResults.revenue}
            >
              <NestedTable fetchEffect={revenue} />
            </CollapseContainerRow>
          )}

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.costOfSales}
              k={"financialResults.costOfSales"}
              isEdit={isEdit}
              firstCell={
                getLabel(t, "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.costOfSales", formType)
              }
              level={3}
            />
          </NestedRow>

          {formType === "Simplified" && (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.percentageToBePaid}
                k={"financialResults.percentageToBePaid"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.percentageToBePaid")
                }
                level={3}
              />
            </NestedRow>)
          }

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.grossProfitTotal}
              k={"financialResults.grossProfitTotal"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.grossProfitTotal"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.businessExpenses}
              k={"financialResults.businessExpenses"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.businessExpenses"
              )}
              level={3}
            />
          </NestedRow>

          {IsConsolidation() ? (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.administrativeExpenses}
                k={"financialResults.administrativeExpenses"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.administrativeExpenses"
                )}
                level={3}
              />
            </NestedRow>
          ) : (
            <CollapseContainerRow
              k={"financialResults.administrativeExpenses"}
              EditRow={EditRow}
              isEdit={isEdit}
              isHorAnEn={isHorAnEn}
              level={3}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.administrativeExpenses"
              )}
              otherCellsTexts={data.financialResults.administrativeExpenses}
            >
              <NestedTable fetchEffect={administrativeExpenses} />
            </CollapseContainerRow>
          )}

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.profitFromSalesTotal}
              k={"financialResults.profitFromSalesTotal"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.profitFromSalesTotal"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.incomeFromParticipation}
              k={"financialResults.incomeFromParticipation"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.incomeFromParticipation"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.interestReceivable}
              k={"financialResults.interestReceivable"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.interestReceivable"
              )}
              level={3}
            />
          </NestedRow>

          {formType !== "Simplified" && (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.percentageToBePaid}
                k={"financialResults.percentageToBePaid"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.percentageToBePaid"
                )}
                level={3}
              />
            </NestedRow>)
          }

          {IsConsolidation() ? (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.otherIncome}
                k={"financialResults.otherIncome"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.otherIncome"
                )}
                level={3}
              />
            </NestedRow>
          ) : (
            <CollapseContainerRow
              k={"financialResults.otherIncome"}
              EditRow={EditRow}
              isEdit={isEdit}
              isHorAnEn={isHorAnEn}
              level={3}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.otherIncome"
              )}
              otherCellsTexts={data.financialResults.otherIncome}
            >
              <NestedTable fetchEffect={otherincome} />
            </CollapseContainerRow>
          )}

          {IsConsolidation() ? (
            <NestedRow style={{ height: "50px" }}>
              <EditRow
                finData={data.financialResults.otherExpenses}
                k={"financialResults.otherExpenses"}
                isEdit={isEdit}
                firstCell={t(
                  "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.otherExpenses"
                )}
                level={3}
              />
            </NestedRow>
          ) : (
            <CollapseContainerRow
              k={"financialResults.otherExpenses"}
              EditRow={EditRow}
              isEdit={isEdit}
              isHorAnEn={isHorAnEn}
              level={3}
              clickableCellText={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.otherExpenses"
              )}
              otherCellsTexts={data.financialResults.otherExpenses}
            >
              <NestedTable fetchEffect={otherExpenses} />
            </CollapseContainerRow>
          )}

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.profitBeforeTaxTotal}
              k={"financialResults.profitBeforeTaxTotal"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.profitBeforeTaxTotal"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.currentIncomeTax}
              k={"financialResults.currentIncomeTax"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.currentIncomeTax"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.discontinuedOperationsProfitNetTax}
              k={"financialResults.discontinuedOperationsProfitNetTax"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.discontinuedOperationsProfitNetTax"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.permanentTax}
              k={"financialResults.permanentTax"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.permanentTax"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.changeTaxLiabilities}
              k={"financialResults.changeTaxLiabilities"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.changeTaxLiabilities"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.changeTaxAssets}
              k={"financialResults.changeTaxAssets"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.changeTaxAssets"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.netIncomeOther}
              k={"financialResults.netIncomeOther"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.netIncomeOther"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.netIncomeTotal}
              k={"financialResults.netIncomeTotal"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.netIncomeTotal"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.resultFromAssets}
              k={"financialResults.resultFromAssets"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.resultFromAssets"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.resultFromOtherOperations}
              k={"financialResults.resultFromOtherOperations"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.resultFromOtherOperations"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.s2530}
              k={"financialResults.s2530"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.s2530"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.financialResultTotal}
              k={"financialResults.financialResultTotal"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.financialResultTotal"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.dilutedEarningsPerShare}
              k={"financialResults.dilutedEarningsPerShare"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.dilutedEarningsPerShare"
              )}
              level={3}
            />
          </NestedRow>

          <NestedRow style={{ height: "50px" }}>
            <EditRow
              finData={data.financialResults.basicEarningsPerShare}
              k={"financialResults.basicEarningsPerShare"}
              isEdit={isEdit}
              firstCell={t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.basicEarningsPerShare"
              )}
              level={3}
            />
          </NestedRow>

          <TableRow>
            <FirstCell level={2}>
              {t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.fnsStatus"
              )}
            </FirstCell>
            {transformedData.fnsStatus.map((el, index) => (
              <TextCell key={index}>
                <div>{el === "" ? "" : t(`FnsStatus.${el}`)}</div>
              </TextCell>
            ))}
          </TableRow>

          <TableRow>
            <FirstCell level={2}>
              {t(
                "finDataRequest.tabs.FinancialStatements.tables.FinancialStatements.taxationSystem"
              )}
            </FirstCell>
            {transformedData.taxationSystem.map((el, index) => (
              <TextCell key={index}>
                <div>{el ? t(`taxationSystem.${el}`) : ""}</div>
              </TextCell>
            ))}
          </TableRow>
        </TableBody>
      </DefaultTable>

      {/* Окно редактирования */}
      {isEdit && selectedCell && (
        <EditCellModal
          selectedCell={selectedCell}
          initialComment={cellComments[`${selectedCell.path}::${transformedData.header[selectedCell.index]}`] || ""}
          onClose={() => setSelectedCell(null)}
          onApply={applyChange}
        />
      )}
    </>
  );
};

export default FinTable2;
